import {
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  StarFilled,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Popconfirm,
  Popover,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  message,
} from "antd";
import { DATE_TIME_FORMAT, COUNTRIES } from "common/constants";
import { formatError } from "common/redux/middleware/queryErrorLogger";
import { camelToWords, snakeToCamelCase, toTitleCase } from "common/utils";
import { DisplayAddress, DisplayComponent } from "components/formItems";
import { useUserType } from "features/auth";
import {
  useCreateDebtorAddressesMutation,
  useDeleteDebtorAddressesMutation,
  useReplicatePacketedDebtorAddressesMutation,
  useUpdateDebtorAddressesMutation,
} from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import moment from "moment-timezone";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useCompactMode } from "providers/compactModeProvider";
import styled from "styled-components";

const StyledCard = styled.div`
  margin-top: 0px;
  margin-left: 8px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  h4 {
    margin: 2px 0;
  }
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledTag = styled(Tag)`
  margin-right: 0;
  font-weight: normal;
`;

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const StyledCompactItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2px 0;
  min-width: 0;
`;

const StyledCompactSubRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 22px;
  flex-wrap: wrap;
`;

const StyledCompactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  .ant-btn-link {
    padding: 0;
    min-width: auto;
    height: auto;
    line-height: 1;
  }
`;

const StyledPrimaryGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const StyledCompactPrimary = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledStarFilled = styled(StarFilled)`
  color: #faad14;
  flex-shrink: 0;
`;

const StyledTrailingActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const StyledForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 4px;
  }
`;

const StyledLeadingIcon = styled(EnvironmentOutlined)`
  color: #8c8c8c;
  flex-shrink: 0;
`;

const StyledInfoIcon = styled(InfoCircleOutlined)`
  color: #1677ff;
  cursor: pointer;
`;

const StyledDescriptions = styled(Descriptions)`
  white-space: nowrap;
`;

export function DebtorAddressInfoCard({
  triggerReplicatePacketedDebtorUpdatesModal = () => {},
  address = undefined,
  isNew = undefined,
  onCreate = undefined,
  onCancel = undefined,
  title = undefined,
  isFirstItem = true,
}) {
  const { isAgencyUserType } = useUserType();
  const { isCompactMode } = useCompactMode();
  const { debtorId } = useParams();
  const [isEditing, setIsEditing] = useState(isNew ?? false);
  const { data: constants } = useFetchBackendConstantsQuery();
  const [form] = Form.useForm();
  const [country, setCountry] = useState(address?.country ?? "US");
  const [updateDebtorAddress, { isLoading: isUpdateLoading }] = useUpdateDebtorAddressesMutation();
  const [createDebtorAddress, { isLoading: isCreateLoading }] = useCreateDebtorAddressesMutation();
  const [deleteDebtorAddress, { isLoading: isDeleteLoading }] = useDeleteDebtorAddressesMutation();
  const [replicatePacketedDebtorAddresses] = useReplicatePacketedDebtorAddressesMutation();
  const {
    data: {
      [PERMISSIONS.DEBTOR_ADDRESS__CREATE]: isAddDebtorAddressAuthorized,
      [PERMISSIONS.DEBTOR_ADDRESS__UPDATE]: isUpdateDebtorAddressAuthorized,
      [PERMISSIONS.DEBTOR_ADDRESS__DELETE]: isDeleteDebtorAddressAuthorized,
    },
  } = useAuthorizations();

  const editableDataSourceTypeValues = constants?.editableDataSourceTypes?.map(
    ({ value }) => value,
  );

  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  const deleteAddress = async () => {
    const result = await deleteDebtorAddress({ debtorId, addressId: address.id });
    if ("data" in result) {
      message.success("Address deleted successfully!");
    }
  };

  const toggleIsEditing = async () => {
    if (isEditing) {
      let values;
      try {
        values = await form.validateFields();
      } catch {
        return null; // Form will handle error messages
      }

      const action = isNew
        ? createDebtorAddress({ debtorId, ...values })
        : updateDebtorAddress({
            debtorId,
            addressId: address.id,
            ...values,
          });
      const result = await action;

      if ("data" in result) {
        message.success("Saved successfully!");
        if (values.isPrimary) {
          triggerReplicatePacketedDebtorUpdatesModal({
            action: replicatePacketedDebtorAddresses,
            dataId: result.data.id,
            newData: result.data.formatted,
            type: "address",
          });
        }
        if (isNew) onCreate();
      }

      if ("error" in result) {
        form.setFields(formatError(result.error));
        message.error(`Could not ${isNew ? "create" : "update"} address`);
        return;
      }
      setIsEditing(false);
    } else {
      setIsEditing(true);
      setCountry(address?.country ?? "US");
      form.setFieldsValue(address);
    }
  };

  const onPrimaryCancel = () => {
    setIsEditing(false);
  };

  const formItemLayout = {
    labelCol: {
      xs: { span: 20 },
      sm: { span: 8 },
    },
  };

  const addressTypes = constants?.addressTypes?.map(({ display, value }) => ({
    value,
    label: display,
  }));

  return (
    <StyledCard>
      {(isFirstItem || isNew || isEditing || !isCompactMode) && (
        <StyledHeader>
          <h4>
            <Space size="small">
              {isNew ? "New Address" : title}
              {address?.dataSource === "skip_trace" && !isEditing && (
                <StyledTag color="magenta">SKIPTRACE</StyledTag>
              )}
              {!isCompactMode && address?.isPrimary && !isEditing && (
                <StyledTag color="gold">PRIMARY</StyledTag>
              )}
            </Space>
          </h4>
          {!isCompactMode &&
            !isNew &&
            !isEditing &&
            isAgencyUserType &&
            (isUpdateDebtorAddressAuthorized || isDeleteDebtorAddressAuthorized) && (
              <Row>
                {isUpdateDebtorAddressAuthorized && (
                  <Button type="link" icon={<EditOutlined />} onClick={toggleIsEditing} />
                )}
                {!address?.isPrimary && isDeleteDebtorAddressAuthorized && (
                  <Popconfirm
                    placement="topRight"
                    okText="Yes"
                    title="Are you sure you want to delete this address?"
                    onConfirm={deleteAddress}
                  >
                    <Button loading={isDeleteLoading} type="link" icon={<DeleteOutlined />} />
                  </Popconfirm>
                )}
              </Row>
            )}
        </StyledHeader>
      )}
      {isEditing && (
        <>
          <StyledForm
            labelAlign="left"
            form={form}
            {...formItemLayout}
            initialValues={{ dataSource: "", country: "US" }}
          >
            <DisplayComponent
              isEditing={isEditing}
              slice={address}
              label="Country"
              name="country"
              rules={[{ required: true }]}
            >
              <Select
                popupMatchSelectWidth={false}
                showSearch
                allowClear
                filterOption={filterOption}
                options={COUNTRIES}
                onChange={(value) => setCountry(value ?? "US")}
              />
            </DisplayComponent>
            <DisplayComponent
              isEditing={isEditing}
              slice={address}
              label="Primary"
              name="isPrimary"
              valuePropName="checked"
              hidden={address?.isPrimary}
            >
              <Checkbox />
            </DisplayComponent>
            <DisplayComponent
              isEditing={isEditing}
              slice={address}
              label="Address"
              name="address1"
            />
            <DisplayComponent
              isEditing={isEditing}
              slice={address}
              label="Address 2"
              name="address2"
            />
            <DisplayComponent
              isEditing={isEditing}
              slice={address}
              label="City"
              name="city"
              rules={[
                { required: true },
                { pattern: /^[^\d]*$/, message: "Numbers not allowed here" },
              ]}
            />
            <DisplayComponent
              isEditing={isEditing}
              slice={address}
              label="State/Province"
              name="state"
              rules={[{ required: true }]}
            >
              {country === "CA" || country === "US" ? (
                <Select
                  popupMatchSelectWidth={false}
                  showSearch
                  filterOption={filterOption}
                  options={(country === "CA"
                    ? constants?.canadianProvinces
                    : constants?.states
                  )?.map((state) => ({
                    label: `${state.display} (${state.value})`,
                    value: state.value,
                  }))}
                />
              ) : (
                <Input />
              )}
            </DisplayComponent>
            <DisplayComponent
              isEditing={isEditing}
              slice={address}
              label="Zip/Postal Code"
              name="zipCode"
              maxLength={country === "US" ? 5 : 7}
              dependencies={["country"]}
              placeholder={country === "CA" ? "A1A 1A1" : ""}
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const selectedCountry = getFieldValue("country");
                    if (selectedCountry !== "US") {
                      return Promise.resolve();
                    }
                    if (!value || /^\d{5}(-\d{4})?$/.test(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Must be a valid zip code"));
                  },
                }),
              ]}
            />
            <DisplayComponent
              isEditing={isEditing}
              slice={address}
              label="Type"
              name="type"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                filterOption={(input, option) =>
                  (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                }
                allowClear
                popupMatchSelectWidth={false}
                options={addressTypes}
              />
            </DisplayComponent>
            <DisplayComponent isEditing={isEditing} slice={address} label="Notes" name="notes">
              <Input.TextArea placeholder="Add Notes" />
            </DisplayComponent>
            {/* Only allow setting of data source if no data source is set, or it is editable (i.e. present in the editableDataSourceTypes) */}
            {(!address?.dataSource ||
              editableDataSourceTypeValues.includes(address?.dataSource)) && (
              <DisplayComponent
                isEditing={isEditing}
                slice={address}
                label="Data Source"
                name="dataSource"
              >
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option.label.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                  popupMatchSelectWidth={false}
                  options={constants?.editableDataSourceTypes?.map(({ display, value }) => ({
                    value,
                    label: display,
                  }))}
                />
              </DisplayComponent>
            )}
          </StyledForm>
          <StyledButtonContainer>
            <Space>
              <Button onClick={isNew ? onCancel : onPrimaryCancel}>Cancel</Button>
              {(isAddDebtorAddressAuthorized || isUpdateDebtorAddressAuthorized) && (
                <Button
                  loading={isCreateLoading || isUpdateLoading}
                  onClick={toggleIsEditing}
                  type="primary"
                >
                  Save
                </Button>
              )}
            </Space>
          </StyledButtonContainer>
        </>
      )}
      {!isEditing && isCompactMode && (
        <StyledCompactItem>
          <StyledCompactRow>
            <StyledLeadingIcon />
            <StyledPrimaryGroup>
              <Tooltip
                title={
                  [address.address1, address.address2].filter(Boolean).join(", ") +
                  (address.type ? ` (${toTitleCase({ str: address.type, delimiter: "_" })})` : "")
                }
              >
                <StyledCompactPrimary>
                  {[address.address1, address.address2].filter(Boolean).join(", ")}
                  {address.type && ` (${toTitleCase({ str: address.type, delimiter: "_" })})`}
                </StyledCompactPrimary>
              </Tooltip>
              {address.isPrimary && (
                <Tooltip title="Primary">
                  <StyledStarFilled />
                </Tooltip>
              )}
            </StyledPrimaryGroup>
            <StyledTrailingActions>
              <Popover
                trigger="hover"
                placement="top"
                content={
                  <StyledDescriptions size="small" column={1} bordered>
                    {address.type ? (
                      <Descriptions.Item label="Type">
                        {toTitleCase({ str: address.type, delimiter: "_" })}
                      </Descriptions.Item>
                    ) : null}
                    {address.isPrimary ? (
                      <Descriptions.Item label="Primary">Yes</Descriptions.Item>
                    ) : null}
                    {address.city ? (
                      <Descriptions.Item label="City">{address.city}</Descriptions.Item>
                    ) : null}
                    {address.state ? (
                      <Descriptions.Item label="State">{address.state}</Descriptions.Item>
                    ) : null}
                    {address.zipCode ? (
                      <Descriptions.Item label="Zip Code">{address.zipCode}</Descriptions.Item>
                    ) : null}
                    {address.country ? (
                      <Descriptions.Item label="Country">{address.country}</Descriptions.Item>
                    ) : null}
                    {address.dataSource ? (
                      <Descriptions.Item label="Data Source">
                        {camelToWords(snakeToCamelCase(address.dataSource))}
                      </Descriptions.Item>
                    ) : null}
                    {address.createdAt ? (
                      <Descriptions.Item label="Created At">
                        {moment(address.createdAt).format(DATE_TIME_FORMAT)}
                      </Descriptions.Item>
                    ) : null}
                    {address.notes ? (
                      <Descriptions.Item label="Notes">{address.notes}</Descriptions.Item>
                    ) : null}
                  </StyledDescriptions>
                }
              >
                <StyledInfoIcon />
              </Popover>
              {isAgencyUserType &&
                (isUpdateDebtorAddressAuthorized || isDeleteDebtorAddressAuthorized) && (
                  <>
                    {isUpdateDebtorAddressAuthorized && (
                      <Button type="link" icon={<EditOutlined />} onClick={toggleIsEditing} />
                    )}
                    {!address?.isPrimary && isDeleteDebtorAddressAuthorized && (
                      <Popconfirm
                        placement="topRight"
                        okText="Yes"
                        title="Are you sure you want to delete this address?"
                        onConfirm={deleteAddress}
                      >
                        <Button loading={isDeleteLoading} type="link" icon={<DeleteOutlined />} />
                      </Popconfirm>
                    )}
                  </>
                )}
            </StyledTrailingActions>
          </StyledCompactRow>
          {(address.city || address.state || address.zipCode) && (
            <StyledCompactSubRow>
              {address.city}
              {address.city && address.state ? ", " : ""}
              {address.state}
              {address.zipCode && ` ${address.zipCode}`}
            </StyledCompactSubRow>
          )}
        </StyledCompactItem>
      )}
      {!isEditing && !isCompactMode && (
        <StyledDiv>
          <DisplayAddress
            isEditing={isEditing}
            slice={address}
            label="Address"
            rules={[{ required: true }]}
          />
          <DisplayComponent
            isEditing={isEditing}
            slice={address}
            label="Data Source"
            name="dataSource"
            outputFormatter={(value) => camelToWords(snakeToCamelCase(value))}
          />
          <DisplayComponent
            isEditing={isEditing}
            slice={address}
            label="Created At"
            name="createdAt"
            outputFormatter={(value) => moment(value).format(DATE_TIME_FORMAT)}
          />
        </StyledDiv>
      )}
    </StyledCard>
  );
}
